import { Injectable } from '@angular/core';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { HttpClient } from '@angular/common/http';
import { isNullOrUndefined } from '@pineapplelab/util';
import { Project } from '@app/models/firebase/project';
import { API_URLS } from '@app/utils/urls';
import { ApiService } from '../api/api.service';

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  constructor(
    private fireStorage: AngularFireStorage,
    private http: HttpClient,
    private api: ApiService,
  ) { }

  uploadFile(path: string, file: File, metadata?: firebase.default.storage.UploadMetadata, projectId?: string) {
    if (isNullOrUndefined(metadata)) {
      metadata = {contentType: file.type};
    } else if (file.type?.startsWith('image/') && metadata.contentType !== file.type) {
      // Callers pass a fixed type (mostly 'image/jpeg') but the image cropper always outputs PNG; the file knows its real type.
      metadata = { ...metadata, contentType: file.type };
    }
    return this.fireStorage.upload(path, file, metadata).then((r) => r.ref.getDownloadURL()).then((url) => {
      if (projectId) {
        this.http.post(`${this.api.apiUrl}${API_URLS.STORAGE.TRACK_STORAGE(projectId)}`, { bytes: file.size }).subscribe();
      }
      return url;
    });
  }

  async saveOrRemoveImagesIfNecessary(file:File, commonPath:string){
    let fileUrl:string;
    if (!isNullOrUndefined( file )) {
      const path = `${commonPath}/image${file.name}`;
      fileUrl = await this.uploadFile( path, file, {contentType: 'image/jpeg'}  );
    } else if (file === null) {
      fileUrl = null;
    }
    return fileUrl;
  }

  // Deletes a previously-uploaded file directly from its download URL. Swallows errors (e.g. the
  // file was already removed, or a stale/foreign URL) so a cleanup failure never blocks a save.
  async deleteFileByUrl(url: string): Promise<void> {
    if (isNullOrUndefined(url)) {
      return;
    }
    try {
      await this.fireStorage.storage.refFromURL(url).delete();
    } catch {
      // best-effort cleanup only
    }
  }

  async saveBannerImage(file:File, projectId:string){
    const commonPath = `${Project.PATH}/${projectId}/`;
    let fileUrl:string;
    let fileExtension = this.getFileExtension(file);
    if (!isNullOrUndefined( file )) {
      const path = `${commonPath}/imageBanner/imageBanner.png`;
      fileUrl = await this.uploadFile( path, file, {contentType: 'image/jpeg'}  );
    } else if (file === null) {
      fileUrl = null;
    }
    return fileUrl;
  }

  uploadBytes(path: string, data: Uint8Array, contentType: string): Promise<string> {
    const blob = new Blob([data as BlobPart], { type: contentType });
    return this.fireStorage.upload(path, blob, { contentType }).then(r => r.ref.getDownloadURL());
  }

  async getSignedUrl(path: string) {
    return this.fireStorage.ref(path).getDownloadURL();
  }
  getFileExtension(file: File): string {
    return file?.name?.split('.').pop() || '';
  }
}
